import { ChangeDetectionStrategy, Component, HostBinding, Input } from '@angular/core';
import { clampParaMilestoneIconTier, defaultParaMilestoneTierStroke } from './para-milestone-icon.util';

export type ParaMilestoneIconSize = 'compact' | 'default' | 'prominent';

@Component({
	selector: 'app-para-milestone-icon',
	templateUrl: './para-milestone-icon.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParaMilestoneIconComponent {
	/** Milestone tier 1–10 (usually `sortOrder` from API). Values outside range are clamped. */
	@Input() tier: number | string | null = 1;
	/** Optional 1-based fallback when tier is unset or invalid (e.g. row index). */
	@Input() tierFallback?: number | null;
	/** Visual scale presets; override with {@link svgClass}. */
	@Input() size: ParaMilestoneIconSize = 'default';

	/** Appends to svg element (Tailwind sizing). Overrides {@link size} when set. */
	@Input() svgClass?: string | null;

	@Input() ariaLabel?: string | null;

	@HostBinding('class.tw-inline-flex') readonly hostFlex = true;
	@HostBinding('class.tw-shrink-0') readonly hostShrink = true;

	get tierIdx(): number {
		return clampParaMilestoneIconTier(this.tier, this.tierFallback ?? 1);
	}

	get resolvedAria(): string {
		return this.ariaLabel?.trim() ?? `Milestone tier ${this.tierIdx}`;
	}

	get tierHexColor(): string {
		return defaultParaMilestoneTierStroke(this.tierIdx);
	}

	get sizeClassResolved(): string {
		if (this.svgClass?.trim()) {
			return this.svgClass.trim();
		}
		switch (this.size) {
			case 'compact':
				/* Status strip chips; still readable next to labels */
				return 'tw-h-6 tw-w-6';
			case 'prominent':
				/* Stat cards / emphasized tiles */
				return 'tw-h-10 tw-w-10';
			default:
				return 'tw-h-8 tw-w-8';
		}
	}
}
